import {
  OrganizationDoc,
  Group,
  GroupMember,
  EligibilityFileDoc,
  WorkoutLog,
  LearningLibraryLog,
  WorkoutLogType,
  LearningLibraryLogType,
  InviteDocument,
} from "@sworkit/api";
import {
  getTypeFromActivityKey,
  workoutLogsToStatsByUserId,
} from "./portal-firestore";
export type PortalAccessType =
  | "curriculumOnly"
  | "orgAdmin"
  | "orgOwner"
  | "none";
export function getPermission(org: OrganizationDoc, uid: string) {
  if (org && org.owner === uid) {
    return "owner";
  } else if (org && org.editors && org.editors.indexOf(uid) !== -1) {
    return "editor";
  } else {
    return null;
  }
}

export const claimsKeyMap = {
  owner: "orgOwner",
  editor: "orgAdmin",
  curriculum: "curriculumOnly",
};

export function getPercent(portion: number, total: number) {
  if (!portion || !total) return 0;
  return Number(((portion / total) * 100).toFixed(0));
}

export function addWorkoutDataToOrgMembers(
  statsByUserId: StatsByUserId,
  originalUsers: GroupMember[],
) {
  const members = originalUsers.map((member) => {
    const memberData = statsByUserId[member.uid];
    return addWorkoutDataToOrgMember(member, memberData);
  });
  return members;
}

export function addWorkoutDataToOrgMember(
  member: GroupMember,
  memberData: GroupMemberStats,
) {
  const orgMember: GroupMemberWithStats = {
    ...member,
    workoutTypeCount: memberData?.workoutTypeCount || {
      strength: 0,
      yoga: 0,
      cardio: 0,
      stretching: 0,
      other: 0,
      meditation: 0,
      nutrition: 0,
      assessment: 0
    },
    assessmentCount: memberData?.assessmentCount || 0,
    assessmentMinutes: memberData?.assessmentMinutes || 0,
    meditationMinutes: memberData?.meditationMinutes || 0,
    workoutSlugCount: memberData?.workoutSlugCount || {},
    activeDaysCount: memberData?.activeDaysCount || 0,
    activeMinutes: memberData?.activeMinutes || 0,
    workoutCount: memberData?.workoutCount || 0,
    meditationCount: memberData?.meditationCount || 0,
    workoutsByDate: memberData?.workoutsByDate || {},
    stepCount: memberData?.stepCount || 0,
    distanceMeters: memberData?.distanceMeters || 0,
    stepsByDate: memberData?.stepsByDate || {},
    learningLibraryByDate: memberData?.learningLibraryByDate || {},
    learningLibraryMinutes: memberData?.learningLibraryMinutes || 0,
    learningLibraryViews: memberData?.learningLibraryViews || 0,
  };
  return orgMember;
}

export function navigateToMissingPermissionsPage(orgId: string) {
  const router = document.querySelector("ion-router");
  return router.push(`organization/${orgId}/missing-permission`);
}

export const chipColors = [
  "primary",
  "secondary",
  "tertiary",
  "success",
  "warning",
  "danger",
  "light",
  "medium",
  "dark",
];

export function sortAlphabetically(
  field: "firstName" | "lastName" | "lastSeenInApp",
  direction: "asc" | "desc",
  a: GroupMember | InviteDocument,
  b: GroupMember | InviteDocument,
) {
  if (!a || !b) return 0;

  // if (
  //   field == 'firstName' &&
  //   a.lastName?.toLowerCase() > b.lastName?.toLowerCase()
  // ) {
  //   return direction === 'asc' ? 1 : -1;
  // }
  // if (
  //   field == 'lastName' &&
  //   a.firstName?.toLowerCase() > b.firstName?.toLowerCase()
  // ) {
  //   return direction === 'asc' ? 1 : -1;
  // }
  if (field == "lastSeenInApp" && a["lastSeenInApp"] > b["lastSeenInApp"]) {
    return direction === "asc" ? 1 : -1;
  }
  if (a[field]?.hasOwnProperty("toLowerCase")) {
    const aName = a[field]?.toLowerCase();
    const bName = b[field]?.toLowerCase();
    if (aName < bName) {
      return direction === "asc" ? -1 : 1;
    }
    if (aName > bName) {
      return direction === "asc" ? 1 : -1;
    }
  }

  const aNum = a[field] || 0;
  const bNum = b[field] || 0;
  if (aNum < bNum) {
    return direction === "asc" ? -1 : 1;
  }
  if (aNum > bNum) {
    return direction === "asc" ? 1 : -1;
  }
  return 0;
}

export const sortMembers = (
  a: GroupMemberWithStats,
  b: GroupMemberWithStats,
  sortMembersByKey: keyof GroupMemberWithStats,
  sortMembersDirection: "asc" | "desc",
) => {
  let aValue = a[sortMembersByKey];
  if (typeof aValue === "string") {
    aValue = aValue.toLowerCase();
  }
  let bValue = b[sortMembersByKey];
  if (typeof bValue === "string") {
    bValue = bValue.toLowerCase();
  }
  if (aValue < bValue) {
    return sortMembersDirection === "asc" ? -1 : 1;
  }
  if (aValue > bValue) {
    return sortMembersDirection === "asc" ? 1 : -1;
  }
  return 0;
};

export function getPortalAccessTypeForOrgId(
  portals: {
    curriculumOnly?: string[];
    orgAdmin?: string[];
    orgOwner?: string[];
  },
  orgPermission: AuthState["orgPermission"],
  orgId: string,
) {
  const portalsList = portalAccess(portals, orgPermission, orgId);
  const portal = portalsList.find((portal) => portal.orgId === orgId);
  return portal?.type || "none";
}

export function showOnlyCurriculumAccess(
  portals: {
    curriculumOnly?: string[];
    orgAdmin?: string[];
    orgOwner?: string[];
  },
  orgPermission?: string,
  orgId?: string,
) {
  const portalsList = portalAccess(portals, orgPermission, orgId);
  const curriculumOrgs = portalsList.filter(
    (portal) => portal.type === "curriculumOnly",
  );
  return (
    curriculumOrgs.length > 0 && curriculumOrgs.length === portalsList.length
  );
}

export function portalAccess(
  portalAccess: {
    curriculumOnly?: string[];
    orgAdmin?: string[];
    orgOwner?: string[];
  },
  orgPermission?: string,
  orgPermissionId?: string,
) {
  const accessMap: { [orgId: string]: PortalAccessType } = {};
  //order of array is important
  for (const typeKey of [
    "orgAdmin",
    "orgOwner",
    "curriculumOnly",
  ] as PortalAccessType[]) {
    if (!portalAccess || !Array.isArray(portalAccess[typeKey])) continue;
    for (const orgId of portalAccess[typeKey]) {
      accessMap[orgId] = typeKey;
    }
  }
  // old portal access is deprecated, but check for it until we decide to remove completely
  // this will overwrite the permission above, which may or may not be desired
  if (orgPermissionId && orgPermission && claimsKeyMap[orgPermission]) {
    accessMap[orgPermissionId] = claimsKeyMap[orgPermission];
  }
  return Object.entries(accessMap).map(([orgId, type]) => ({ orgId, type }));
}

export function groupIdFilter(member: GroupMember, groupId: string) {
  if (groupId && groupId === "ALL_MEMBERS") {
    return true;
  }
  if (groupId === "NO_GROUP" && (!member.groups || !member.groups.length)) {
    return true;
  }
  if (groupId && !member.groups) {
    return false;
  }
  if (groupId && member.groups?.includes(groupId)) {
    return true;
  }
  return false;
}
//todo write tests
export function searchMembersFilter(
  member: GroupMemberInvite | EligibilityFileDoc,
  searchString: string,
) {
  const searchValue = searchString?.toLowerCase();
  if (!searchValue) return true;
  const lowerCaseName = `${member.firstName?.toLowerCase() || ""} ${
    member.lastName?.toLowerCase() || ""
  }`;
  const nameMatch = lowerCaseName?.indexOf(searchValue) > -1;
  if (nameMatch) return true;
  const emailMatch = member?.email?.toLowerCase()?.includes(searchValue);
  return Boolean(emailMatch);
}

export function searchEligibilityFileFilter(
  member: EligibilityFileDoc,
  searchString: string,
) {
  const searchValue = searchString?.toLowerCase();
  if (!searchValue) return true;
  const lowerCaseName = `${member.firstName?.toLowerCase() || ""} ${
    member.lastName?.toLowerCase() || ""
  }`;
  const nameMatch = lowerCaseName?.indexOf(searchValue) > -1;
  if (nameMatch) return true;
  const emailMatch = member?.email?.toLowerCase()?.includes(searchValue);
  const uniqueIdMatch = member?.uniqueId?.toLowerCase()?.includes(searchValue);
  return Boolean(emailMatch) || Boolean(uniqueIdMatch);
}

export const sortGroupsAlphabetically = (a: Group, b: Group) => {
  if (!a || !b) return 0;
  if (a.name > b.name) {
    return 1;
  } else if (a.name == b.name) {
    return 0;
  } else {
    return -1;
  }
};

export function getCombinedStatsFromMemberStats(members: GroupMemberStats[]) {
  let activeMemberCount = 0;
  let totalMinutes = 0;
  let totalWorkouts = 0;
  let totalMeditations = 0;
  let totalUniqueMemberActiveDays = 0;
  let totalSteps = 0;
  let totalDistanceMeters = 0;
  const workoutTypeCount: GroupMemberStats["workoutTypeCount"] = {
    strength: 0,
    yoga: 0,
    cardio: 0,
    stretching: 0,
    other: 0,
    meditation: 0,
    nutrition: 0,
    assessment: 0
  };
  members?.map((member) => {
    if (
      member.activeDaysCount > 0 ||
      member.stepCount ||
      member.distanceMeters
    ) {
      activeMemberCount += 1;
      totalUniqueMemberActiveDays += member.activeDaysCount;
    }
    totalMinutes += member.activeMinutes;
    totalMinutes += member.learningLibraryMinutes; //@todo remove if necessary
    totalWorkouts += member.workoutCount;
    totalMeditations += member.meditationCount;
    totalDistanceMeters += member.distanceMeters;
    totalSteps += member.stepCount;
    Object.keys(workoutTypeCount).map((type) => {
      workoutTypeCount[type] += member.workoutTypeCount[type];
    });
  });
  return {
    activeMemberCount,
    totalMinutes,
    totalWorkouts,
    totalMeditations,
    workoutTypeCount,
    totalUniqueMemberActiveDays,
    totalSteps,
    totalDistanceMeters,
  };
}

export function getMemberStatsFromWorkoutLogs(
  uid,
  workoutLogs: WorkoutLog[],
  learningLibraryLogs: LearningLibraryLog[],
) {
  const logs = workoutLogs.map((log) => {
    const newLog: SimpleLog = {
      uid,
      ...log,
      type: getTypeFromActivityKey(log) as WorkoutLogType,
    };
    return newLog;
  });
  const learningLogs = learningLibraryLogs.map((learningLibraryLog) => {
    const newLog: SimpleLog = {
      uid,
      ...learningLibraryLog,
      type: learningLibraryLog.type as LearningLibraryLogType,
    };
    return newLog;
  });
  const statsByUid = workoutLogsToStatsByUserId([...logs, ...learningLogs]);
  const stats = statsByUid[uid];
  const groupMemberWithStats: GroupMemberWithStats = {
    uid: uid,
    firstName: "",
    lastName: "",
    ...stats,
  };
  return groupMemberWithStats;
}

export function getGroupMemberId(obj: GroupMemberInvite) {
  if (typeof obj === "object" && "inviteId" in obj) {
    return obj?.inviteId;
  } else {
    return obj?.uid;
  }
}

export function findObjectDifferences(obj1, obj2, prefix = "") {
  const differences = {};
  // If both are dates, compare by time
  if (obj1 instanceof Date && obj2 instanceof Date) {
    if (obj1.getTime() !== obj2.getTime()) {
      differences[prefix] = { obj1, obj2 };
    }
    return differences;
  }
  // Check if both objects are of the same type
  if (typeof obj1 !== typeof obj2) {
    differences[prefix] = { obj1, obj2 };
    return differences;
  }

  // Check if the objects are arrays
  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2)) {
      differences[prefix] = { obj1, obj2 };
      return differences;
    }
    if (obj1.length !== obj2.length) {
      differences[prefix] = { obj1, obj2 };
      return differences;
    }
    for (let i = 0; i < obj1.length; i++) {
      const subDifferences = findObjectDifferences(
        obj1[i],
        obj2[i],
        `${prefix}[${i}]`,
      );
      if (Object.keys(subDifferences).length > 0) {
        differences[`${prefix}[${i}]`] = subDifferences;
      }
    }
    return differences;
  }

  // Check if the objects are non-null objects
  if (typeof obj1 === "object" && obj1 !== null && obj2 !== null) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    const allKeys = new Set([...keys1, ...keys2]);

    for (const key of allKeys) {
      const subDifferences = findObjectDifferences(
        obj1[key],
        obj2[key],
        `${prefix}.${key}`,
      );
      if (Object.keys(subDifferences).length > 0) {
        differences[`${prefix}.${key}`] = subDifferences;
      }
    }

    return differences;
  }

  // Check if primitive values match
  if (obj1 !== obj2) {
    differences[prefix] = { obj1, obj2 };
  }
  return differences;
}

export function getUniqueKeys(arrayOfObjects: any[]) {
  const keySet = new Set();

  arrayOfObjects.forEach((obj) => {
    Object.keys(obj).forEach((key) => {
      keySet.add(key);
    });
  });

  return keySet;
}

export function onOrgMemberChangeIsSignificant(
  modified: GroupMember[],
  orgMembersByUid,
) {
  if (modified.length === 0) {
    return true;
  }
  //compare last in app to see if we should update org members
  const allChangedFields = getUniqueKeys(
    modified.map((modifiedMember) =>
      findObjectDifferences(
        modifiedMember,
        orgMembersByUid?.[modifiedMember.uid],
      ),
    ),
  );
  allChangedFields.delete(".lastSeenInApp");
  return allChangedFields.size == 0;
}

export const objectsDeeplyEqual = <T>(a: T, b: T): boolean => {
  if (a === b) {
    return true;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }

  const aKeys = Object.keys(a as object);
  const bKeys = Object.keys(b as object);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  return aKeys.every((key) =>
    objectsDeeplyEqual((a as any)[key], (b as any)[key]),
  );
};

export function checkOrgSecurity(
  org: OrganizationDoc,
  flag:
    | "showAllMembersReport"
    | "showGroups"
    | "showMemberReport"
    | "showTopTenMembers"
    | "showTopTenWorkouts"
    | "hideAllMemberData",
) {
  if (!org || org?.hideAllMemberData || !flag) {
    return false;
  }

  return flag == "hideAllMemberData" ? true : org[flag];
}
